import { useCallback, useEffect, useMemo, useState } from 'react';
import { PackagePlus, Loader2, AlertTriangle, Info, Ruler } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils/cn';
import { useToast } from '@/lib/hooks/use-toast';
import { importTrackingService } from '../services/import-tracking-service';
import type { PendingStowItem } from '../types/import-tracking.types';

/**
 * Estibar en el contenedor los productos que ya están en la orden.
 *
 * Antes había que volver a cargar cada producto a mano en el contenedor:
 * descripción, largo, ancho, alto, peso, cantidad. Los mismos datos que ya
 * habían entrado con la proforma. Pedir el mismo dato dos veces no es solo
 * trabajo de más — garantiza que en algún momento las dos copias no coincidan, y
 * ahí el 3D dice que la carga entra con medidas que no son las reales.
 *
 * Muestra la cantidad **pendiente**: si de 40 aires ya se estibaron 25 en el
 * primer contenedor, quedan 15. Un embarque real usa dos o tres contenedores.
 */

export interface StowFromOrderPanelProps {
  containerId: string;
  onStowed: () => void;
}

export function StowFromOrderPanel({ containerId, onStowed }: StowFromOrderPanelProps) {
  const { toast } = useToast();
  const [items, setItems] = useState<PendingStowItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  /** Cuántas unidades estibar de cada producto. Arranca en todo lo pendiente. */
  const [amounts, setAmounts] = useState<Record<string, string>>({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await importTrackingService.getPendingStowItems(containerId);
      setItems(r.items);
      setAmounts(
        Object.fromEntries(r.items.map((i) => [i.orderItemId, String(i.pendingQuantity)]))
      );
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [containerId]);

  useEffect(() => {
    void load();
  }, [load]);

  const stowable = useMemo(() => items.filter((i) => !i.missingDimensions), [items]);
  const withoutDims = useMemo(() => items.filter((i) => i.missingDimensions), [items]);

  const selected = useMemo(
    () =>
      stowable
        .map((i) => ({ orderItemId: i.orderItemId, quantity: Number(amounts[i.orderItemId] ?? 0) }))
        .filter((i) => Number.isFinite(i.quantity) && i.quantity > 0),
    [stowable, amounts]
  );

  const stow = async () => {
    setSaving(true);
    try {
      const r = await importTrackingService.stowFromOrder(containerId, selected);
      toast({
        title: `${r.stowed} producto(s) estibados`,
        description:
          r.skipped.length > 0
            ? `No entraron: ${r.skipped.map((s) => `${s.description} (${s.reason})`).join('; ')}.`
            : 'Ya se pueden acomodar en el contenedor.',
        variant: r.skipped.length > 0 ? 'warning' : 'success',
      });
      await load();
      onStowed();
    } catch (err) {
      toast({
        title: 'No se pudo estibar',
        description:
          (err as { response?: { data?: { error?: string } } })?.response?.data?.error ??
          'Ocurrió un error.',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Skeleton className="h-24 w-full" />;

  if (items.length === 0) {
    return (
      <p className="flex items-start gap-1.5 rounded-lg bg-muted/40 px-3 py-2.5 text-xs text-muted-foreground">
        <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
        Todos los productos de la orden ya están en algún contenedor. Si falta algo, cargalo primero
        en la pestaña Productos de la orden.
      </p>
    );
  }

  return (
    <div className="space-y-3 rounded-xl border bg-muted/20 p-3">
      <div>
        <h4 className="flex items-center gap-1.5 text-sm font-semibold">
          <PackagePlus className="h-4 w-4 text-prixia-blue" />
          Traer los productos de la orden
        </h4>
        <p className="mt-0.5 text-xs text-muted-foreground">
          Ya están cargados con sus medidas. Elegí cuántas unidades van en este contenedor.
        </p>
      </div>

      <div className="overflow-x-auto rounded-lg border bg-background">
        <table className="w-full min-w-[520px] text-sm">
          <thead className="bg-muted/50">
            <tr className="border-b text-xs">
              <th className="px-3 py-2 text-left font-medium">Producto</th>
              <th className="px-3 py-2 text-right font-medium">Pendiente</th>
              <th className="px-3 py-2 text-left font-medium">Medidas</th>
              <th className="w-24 px-3 py-2 text-right font-medium">Estibar</th>
            </tr>
          </thead>
          <tbody>
            {items.map((item) => (
              <tr
                key={item.orderItemId}
                className={cn(
                  'border-b last:border-0',
                  item.missingDimensions && 'bg-alerta-suave'
                )}
              >
                <td className="px-3 py-2">
                  <p className="text-xs font-medium">{item.description}</p>
                  {item.alreadyStowed > 0 && (
                    <p className="text-[11px] text-muted-foreground">
                      {item.alreadyStowed} de {item.totalQuantity} ya estibadas
                    </p>
                  )}
                </td>
                <td className="px-3 py-2 text-right font-mono text-xs tabular-nums">
                  {item.pendingQuantity}
                </td>
                <td className="px-3 py-2 text-[11px]">
                  {item.missingDimensions ? (
                    <span className="inline-flex items-center gap-1 text-alerta">
                      <Ruler className="h-3 w-3" />
                      faltan medidas
                    </span>
                  ) : (
                    <span className="font-mono text-muted-foreground">
                      {item.lengthCm}×{item.widthCm}×{item.heightCm} cm · {item.weightKg ?? 0} kg
                    </span>
                  )}
                </td>
                <td className="px-3 py-2">
                  <Input
                    type="number"
                    min={0}
                    max={item.pendingQuantity}
                    value={amounts[item.orderItemId] ?? ''}
                    disabled={item.missingDimensions}
                    onChange={(e) =>
                      setAmounts((prev) => ({ ...prev, [item.orderItemId]: e.target.value }))
                    }
                    className="h-8 text-right font-mono text-xs"
                    aria-label={`Unidades de ${item.description} a estibar`}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {withoutDims.length > 0 && (
        <p className="flex items-start gap-1.5 rounded-md border border-alerta-borde bg-alerta-suave px-3 py-2 text-xs text-foreground">
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-alerta" />
          <span>
            <strong>
              {withoutDims.length} producto(s) sin medidas no se pueden estibar todavía.
            </strong>{' '}
            Sin largo, ancho y alto el armado no puede ubicarlos ni prorratear el flete por volumen.
            Cargá las medidas en la pestaña Productos de la orden.
          </span>
        </p>
      )}

      <Button size="sm" disabled={saving || selected.length === 0} onClick={() => void stow()}>
        {saving ? (
          <>
            <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
            Estibando…
          </>
        ) : (
          <>
            <PackagePlus className="mr-1.5 h-4 w-4" />
            Estibar {selected.reduce((s, i) => s + i.quantity, 0)} unidad(es)
          </>
        )}
      </Button>
    </div>
  );
}
